import { useState, useEffect } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { pickerStyle, selectStyle } from "../MUIStyles";
import { FormControl, MenuItem, Select, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useMyContext } from "../context";
import useFilter from "../hooks/useFilter";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const FilterSearch = ({ filterBy, type }) => {
  const { t } = useTranslation();
  const { handleOpenModal } = useMyContext();
  const { setFilter, params, setDriverFilter } = useFilter();

  // Custom Calendar Icon component
  const CustomCalendarIcon = () => (
    <img src="/img/calender.svg" alt="Calendar" className="w-5 h-5" />
  );

  // Local state for dropdowns
  const [values, setValues] = useState({});

  // Local state for search input
  const [searchInput, setSearchInput] = useState(params.search || "");

  // Local state for date pickers - use Dayjs objects or null
  const [dateFromValue, setDateFromValue] = useState(null);
  const [dateToValue, setDateToValue] = useState(null);
  const [dateValue, setDateValue] = useState(null);
  const [yearValue, setYearValue] = useState(null); // New state for year-only picker
  const [dateFormat, setDateFormat] = useState("day"); // 'day', 'month', 'year'

  // Sync dropdowns from URL when mounted
  useEffect(() => {
    const initialValues = {};
    filterBy?.forEach((item) => {
      const key = item?.label?.toLowerCase();
      if (params[key]) {
        // Convert 1/0 back to yes/no for display
        if (item?.label?.toLowerCase() === "has_inspection") {
          initialValues[item.label] =
            params[key] === "1"
              ? "yes"
              : params[key] === "0"
                ? "no"
                : params[key];
        } else {
          initialValues[item.label] = params[key];
        }
      }
    });
    setValues(initialValues);

    // Set date_from value from params - convert to Dayjs object
    if (params.date_from) {
      try {
        const dayjsObj = dayjs(params.date_from, "YYYY-MM-DD");
        if (dayjsObj.isValid()) {
          setDateFromValue(dayjsObj);
        } else {
          setDateFromValue(null);
        }
      } catch (error) {
        console.error("Error parsing date_from:", error);
        setDateFromValue(null);
      }
    } else {
      setDateFromValue(null);
    }

    // Set date_to value from params - convert to Dayjs object
    if (params.date_to) {
      try {
        const dayjsObj = dayjs(params.date_to, "YYYY-MM-DD");
        if (dayjsObj.isValid()) {
          setDateToValue(dayjsObj);
        } else {
          setDateToValue(null);
        }
      } catch (error) {
        console.error("Error parsing date_to:", error);
        setDateToValue(null);
      }
    } else {
      setDateToValue(null);
    }

    // Set date value from params - detect format and convert to Dayjs object
    if (params.date) {
      try {
        // Detect format based on input length
        const dateStr = params.date;
        let dayjsObj = null;
        let detectedFormat = "day";

        if (dateStr.match(/^\d{4}$/)) {
          // Year only: YYYY
          dayjsObj = dayjs(dateStr, "YYYY");
          detectedFormat = "year";
        } else if (dateStr.match(/^\d{4}-\d{2}$/)) {
          // Year-Month: YYYY-MM
          dayjsObj = dayjs(dateStr, "YYYY-MM");
          detectedFormat = "month";
        } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Full date: YYYY-MM-DD
          dayjsObj = dayjs(dateStr, "YYYY-MM-DD");
          detectedFormat = "day";
        }

        if (dayjsObj && dayjsObj.isValid()) {
          setDateValue(dayjsObj);
          setDateFormat(detectedFormat);
        } else {
          setDateValue(null);
          setDateFormat("day");
        }
      } catch (error) {
        console.error("Error parsing date:", error);
        setDateValue(null);
        setDateFormat("day");
      }
    } else {
      setDateValue(null);
      setDateFormat("day");
    }

    // Set year value from params - convert to Dayjs object
    if (params.year) {
      try {
        const dayjsObj = dayjs(params.year, "YYYY");
        if (dayjsObj.isValid()) {
          setYearValue(dayjsObj);
        } else {
          setYearValue(null);
        }
      } catch (error) {
        console.error("Error parsing year:", error);
        setYearValue(null);
      }
    } else {
      setYearValue(null);
    }
  }, [filterBy, params]);

  // Sync search input when URL changes externally
  useEffect(() => {
    setSearchInput(params.search || "");
  }, [params.search]);

  // Debounce search → update URL
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilter({ search: searchInput || "" });
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput, setFilter]);

  // Handle date_from change
  const handleDateFromChange = (newDate) => {
    setDateFromValue(newDate);

    if (newDate && newDate.isValid()) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      setFilter({ date_from: formattedDate });
    } else {
      setFilter({ date_from: "" });
    }
  };

  // Handle date_to change
  const handleDateToChange = (newDate) => {
    setDateToValue(newDate);

    if (newDate && newDate.isValid()) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      setFilter({ date_to: formattedDate });
    } else {
      setFilter({ date_to: "" });
    }
  };

  // Handle single date change with format detection
  const handleDateChange = (newDate) => {
    setDateValue(newDate);

    if (newDate && newDate.isValid()) {
      let formattedDate = "";

      switch (dateFormat) {
        case "year":
          formattedDate = newDate.format("YYYY");
          break;
        case "month":
          formattedDate = newDate.format("YYYY-MM");
          break;
        case "day":
        default:
          formattedDate = newDate.format("YYYY-MM-DD");
          break;
      }

      setFilter({ date: formattedDate });
    } else {
      setFilter({ date: "" });
    }
  };

  // Handle year-only change
  const handleYearChange = (newDate) => {
    setYearValue(newDate);

    if (newDate && newDate.isValid()) {
      const formattedYear = newDate.format("YYYY");
      setFilter({ year: formattedYear });
    } else {
      setFilter({ year: "" });
    }
  };

  // Handle format change
  const handleFormatChange = (format) => {
    setDateFormat(format);

    if (dateValue && dateValue.isValid()) {
      let formattedDate = "";

      switch (format) {
        case "year":
          formattedDate = dateValue.format("YYYY");
          break;
        case "month":
          formattedDate = dateValue.format("YYYY-MM");
          break;
        case "day":
        default:
          formattedDate = dateValue.format("YYYY-MM-DD");
          break;
      }

      setFilter({ date: formattedDate });
    }
  };

  // Clear date_from selection
  const handleClearDateFrom = () => {
    setDateFromValue(null);
    setFilter({ date_from: "" });
  };

  // Clear date_to selection
  const handleClearDateTo = () => {
    setDateToValue(null);
    setFilter({ date_to: "" });
  };

  // Clear single date selection
  const handleClearDate = () => {
    setDateValue(null);
    setDateFormat("day");
    setFilter({ date: "" });
  };

  // Clear year selection
  const handleClearYear = () => {
    setYearValue(null);
    setFilter({ year: "" });
  };

  // Handle regular dropdown change
  const handleChange = (key, val) => {
    const newValues = { ...values, [key]: val };
    setValues(newValues);

    const paramKey = key.toLowerCase();
    let paramValue = val || "";

    // Special handling for has_inspection: yes → 1, no → 0
    if (paramKey === "has_inspection") {
      if (val === "yes") {
        paramValue = "1";
      } else if (val === "no") {
        paramValue = "0";
      }
    }

    setFilter({ [paramKey]: paramValue });
  };

  // Handle driver filter change
  const handleDriverChange = (val) => {
    setDriverFilter(val);

    // Update local values state for driver
    setValues((prev) => ({
      ...prev,
      driver_id: val,
    }));
  };

  // Reset all filters including dates
  const handleReset = () => {
    setValues({});
    setDateFromValue(null);
    setDateToValue(null);
    setDateValue(null);
    setYearValue(null);
    setDateFormat("day");
    const resetParams = {};
    filterBy?.forEach((item) => {
      const key = item.label.toLowerCase();
      resetParams[key] = "";
    });
    resetParams.date_from = "";
    resetParams.date_to = "";
    resetParams.date = "";
    resetParams.year = "";
    setFilter(resetParams);
  };

  // Check if we have date_from filter
  const hasDateFromFilter = filterBy?.some(
    (item) => item?.label?.toLowerCase() === "date_from",
  );

  // Check if we have date_to filter
  const hasDateToFilter = filterBy?.some(
    (item) => item?.label?.toLowerCase() === "date_to",
  );

  // Check if we have a single date filter
  const hasDateFilter = filterBy?.some(
    (item) => item?.label?.toLowerCase() === "date",
  );

  // Check if we have a year filter
  const hasYearFilter = filterBy?.some(
    (item) => item?.label?.toLowerCase() === "year",
  );

  // Filter out date filters from dropdown filters
  const dropdownFilters =
    filterBy?.filter(
      (item) =>
        item?.label?.toLowerCase() !== "date_from" &&
        item?.label?.toLowerCase() !== "date_to" &&
        item?.label?.toLowerCase() !== "date" &&
        item?.label?.toLowerCase() !== "year",
    ) || [];

  // Get appropriate date format for picker
  const getDateFormat = () => {
    switch (dateFormat) {
      case "year":
        return "YYYY";
      case "month":
        return "MM/YYYY";
      case "day":
      default:
        return "DD/MM/YYYY";
    }
  };

  // Get views for the date picker based on format
  const getPickerViews = () => {
    switch (dateFormat) {
      case "year":
        return ["year"];
      case "month":
        return ["year", "month"];
      case "day":
      default:
        return ["year", "month", "day"];
    }
  };

  // Helper function to check if item is a driver filter
  const isDriverFilter = (item) => {
    if (item?.label?.toLowerCase() !== "driver_id") return false;

    // Check if options exist and first option has id and name properties
    if (!item?.options || item.options.length === 0) return false;

    const firstOption = item.options[0];
    return (
      typeof firstOption === "object" &&
      firstOption !== null &&
      "id" in firstOption &&
      "name" in firstOption
    );
  };

  // Helper function to check if item is a currency filter with object options
  const isCurrencyFilter = (item) => {
    if (item?.label?.toLowerCase() !== "currency_id") return false;

    // Check if options exist and first option has id and name properties
    if (!item?.options || item.options.length === 0) return false;

    const firstOption = item.options[0];
    return (
      typeof firstOption === "object" &&
      firstOption !== null &&
      "id" in firstOption &&
      "name" in firstOption
    );
  };

  // Helper function to check if item has object options (id/name)
  const hasObjectOptions = (item) => {
    if (!item?.options || item.options.length === 0) return false;

    const firstOption = item.options[0];
    return (
      typeof firstOption === "object" &&
      firstOption !== null &&
      "id" in firstOption &&
      "name" in firstOption
    );
  };

  // Get the correct value for a filter
  const getFilterValue = (item) => {
    const key = item.label.toLowerCase();

    if (isDriverFilter(item)) {
      return params.driver_id || "";
    } else if (hasObjectOptions(item)) {
      // For filters with object options (like currency_id with id/name objects)
      return params[key] || "";
    } else {
      // For regular filters
      return values[item.label] || "";
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="flex lg:flex-row flex-col lg:items-center justify-between gap-5">
        {type !== "carRequests" && (
          <div className="flex lg:flex-row flex-col gap-3 lg:items-center">
            <h3 className="font-bold text-xl">{t("filter.filterBy")}</h3>

            <div className="flex items-center gap-5 flex-wrap">
              {/* Year-Only Date Picker */}
              {hasYearFilter && (
                <div className="relative flex flex-col gap-1 min-w-[180px]">
                  <h3 className="font-semibold">{t("filter.year")}</h3>
                  <div className="relative">
                    <DatePicker
                      value={yearValue}
                      onChange={handleYearChange}
                      slots={{
                        openPickerIcon: CustomCalendarIcon,
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: pickerStyle,
                        },
                      }}
                      views={["year"]}
                      format="YYYY"
                      reduceAnimations={true}
                      openTo="year"
                    />

                    {/* Clear button for year picker */}
                    {yearValue && (
                      <IconButton
                        onClick={handleClearYear}
                        size="small"
                        sx={{
                          position: "absolute",
                          right: "40px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#9CA3AF",
                          "&:hover": {
                            color: "#6B7280",
                          },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </div>
              )}

              {/* Single Date Picker with Format Selection */}
              {hasDateFilter && (
                <div className="flex flex-col gap-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{t("filter.date")}</h3>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={dateFormat}
                        onChange={(e) => handleFormatChange(e.target.value)}
                        sx={{
                          ...selectStyle,
                          height: "30px",
                          fontSize: "12px",
                          "& .MuiSelect-select": {
                            padding: "4px 8px",
                          },
                        }}
                      >
                        <MenuItem value="day">Full Date</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="year">Year</MenuItem>
                      </Select>
                    </FormControl>
                  </div>

                  <div className="relative">
                    <DatePicker
                      value={dateValue}
                      onChange={handleDateChange}
                      slots={{
                        openPickerIcon: CustomCalendarIcon,
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: pickerStyle,
                        },
                      }}
                      format={getDateFormat()}
                      views={getPickerViews()}
                      reduceAnimations={true}
                    />

                    {/* Clear button for single date picker */}
                    {dateValue && (
                      <IconButton
                        onClick={handleClearDate}
                        size="small"
                        sx={{
                          position: "absolute",
                          right: "40px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#9CA3AF",
                          "&:hover": {
                            color: "#6B7280",
                          },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </div>
              )}

              {/* Date From Picker */}
              {hasDateFromFilter && (
                <div className="relative flex flex-col gap-1 min-w-[180px]">
                  <h3 className="font-semibold">{t("filter.dateFrom")}</h3>
                  <DatePicker
                    value={dateFromValue}
                    onChange={handleDateFromChange}
                    slots={{
                      openPickerIcon: CustomCalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: pickerStyle,
                      },
                    }}
                    format="DD/MM/YYYY"
                    maxDate={dateToValue || undefined} // Optional: limit max date to date_to
                  />

                  {/* Clear button for date_from picker */}
                  {dateFromValue && (
                    <IconButton
                      onClick={handleClearDateFrom}
                      size="small"
                      sx={{
                        position: "absolute",
                        right: "40px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9CA3AF",
                        "&:hover": {
                          color: "#6B7280",
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              )}

              {/* Date To Picker */}
              {hasDateToFilter && (
                <div className="relative flex flex-col gap-1 min-w-[180px]">
                  <h3 className="font-semibold">{t("filter.dateTo")}</h3>
                  <DatePicker
                    value={dateToValue}
                    onChange={handleDateToChange}
                    slots={{
                      openPickerIcon: CustomCalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: pickerStyle,
                      },
                    }}
                    format="DD/MM/YYYY"
                    minDate={dateFromValue || undefined} // Optional: limit min date to date_from
                  />

                  {/* Clear button for date_to picker */}
                  {dateToValue && (
                    <IconButton
                      onClick={handleClearDateTo}
                      size="small"
                      sx={{
                        position: "absolute",
                        right: "40px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9CA3AF",
                        "&:hover": {
                          color: "#6B7280",
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              )}

              {/* Dropdown filters - including driver filter */}
              {dropdownFilters?.map((item) => {
                const hasObjects = hasObjectOptions(item);
                const isDriver = isDriverFilter(item);

                return (
                  <FormControl key={item.label} className="flex-wrap">
                    <Select
                      value={getFilterValue(item)}
                      displayEmpty
                      onChange={(e) => {
                        if (isDriver) {
                          handleDriverChange(e.target.value);
                        } else {
                          handleChange(item.label, e.target.value);
                        }
                      }}
                      sx={selectStyle}
                    >
                      <MenuItem value="">
                        <span>{item.label}</span>
                      </MenuItem>

                      {hasObjects
                        ? // Options with id/name objects (driver, currency, user, etc.)
                          item.options.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))
                        : // Regular options (strings or numbers)
                          item?.options?.map((op) => {
                            // Handle both string and number options
                            const optionValue =
                              typeof op === "object" ? op.id : op;
                            const optionLabel =
                              typeof op === "object" ? op.name : op;

                            return (
                              <MenuItem key={optionValue} value={optionValue}>
                                {optionLabel}
                              </MenuItem>
                            );
                          })}
                    </Select>
                  </FormControl>
                );
              })}
            </div>

            {(Object.keys(values).length > 0 ||
              dateFromValue ||
              dateToValue ||
              dateValue ||
              yearValue) && (
              <button
                onClick={handleReset}
                className="text-sm text-red-500 hover:text-red-700 underline cursor-pointer"
              >
                {t("filter.reset")}
              </button>
            )}
          </div>
        )}

        {type !== "wallets" &&
          type !== "finance" &&
          type !== "occasions" &&
          type !== "rewardTransactions" && (
            <div className="flex items-center gap-2 rounded-[50px] bg-fifth lg:w-[300px] w-full h-[50px] px-5">
              <img src="/img/search.svg" alt="" />

              <input
                type="search"
                name="search"
                id="search"
                className="outline-none w-full h-full placeholder:text-black text-sm"
                placeholder={
                  type === "appVersions"
                    ? t("filter.searchVersionId")
                    : t("filter.search")
                }
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />

              {type === "trips" && (
                <button
                  onClick={() => handleOpenModal("search")}
                  className="cursor-pointer"
                >
                  <img
                    alt=""
                    src="/img/person.svg"
                    className="border-l border-fourth pl-3"
                  />
                </button>
              )}
            </div>
          )}
      </div>
    </LocalizationProvider>
  );
};

export default FilterSearch;
